import { jsx } from "panel";
import { ChartTooltipEl } from "common/components/chart-tooltip/panel";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { ScrollControlEl } from "common/widgets/scroll-control";
import { ChartLegendEl } from "common/components/chart-legend/panel";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { Placement } from "common/components/tooltip/types";
import { formatMetricNumber } from "common/util";
import { ChartLegendTheme } from "common/components/chart-legend/constants";
import { getAccessibleForegroundColor } from "common/util/colors";
import { LinkTooltipPanel } from "./link-tooltip-panel";
const NodeLabel = (props) => {
  const node = props.node;
  const nodeAttributes = props.nodeAttributes;
  const isAnchorNode = node.isAnchorNode();
  const nodeLabel = isAnchorNode ? props.getAnchorLabelOverride(node.getAnchorIndex(), node.eventString) : props.renameEvents(node.eventString);
  const labelIcon = props.getLabelIcon(node);
  const weight = formatMetricNumber(node.totalCount / props.anchorStepWeight * 100);
  const totalCount = formatMetricNumber(node.totalCount);
  const negativeLift = node.conversionRateChange < 0;
  const lift = formatMetricNumber(Math.abs(node.conversionRateChange));
  return /* @__PURE__ */ jsx("div", { sel: ".sankey-node-label-wrapper", key: `label-${node.id}` }, /* @__PURE__ */ jsx(
    "div",
    {
      sel: ".sankey-node-label",
      props: { node, nodeAttributes },
      class: {
        "disable-click": nodeAttributes.disableClick,
        "is-in-top-path": nodeAttributes.isInTopPath
      }
    },
    /* @__PURE__ */ jsx("div", { sel: ".sankey-node-label-content" }, /* @__PURE__ */ jsx("div", { sel: ".sankey-node-name-wrapper" }, /* @__PURE__ */ jsx(null, null, !props.disableAnchorEventLabels && labelIcon && /* @__PURE__ */ jsx("div", { sel: ".sankey-node-icons" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: labelIcon } })), /* @__PURE__ */ jsx("div", { sel: ".sankey-label-content" }, /* @__PURE__ */ jsx("div", { sel: ".sankey-node-name" }, nodeLabel, /* @__PURE__ */ jsx(
      TooltipEl,
      {
        attrs: { placement: Placement.RightCenter, "max-width": `300px` }
      },
      `${nodeLabel}`
    )), /* @__PURE__ */ jsx("div", { sel: ".sankey-label-percentages" }, /* @__PURE__ */ jsx("div", { sel: ".sankey-node-weight" }, /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".sankey-node-weight-percentage" }, `${weight}%`), /* @__PURE__ */ jsx("div", { sel: ".sankey-node-weight-count" }, totalCount), /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: Placement.RightCenter, "max-width": `300px` } }, props.getWeightTooltipText(
      weight,
      node.step,
      node.section,
      nodeAttributes.stepType,
      nodeAttributes.anchorStepIndex,
      nodeLabel,
      isAnchorNode
    )))), !props.disableLift && node.hasLift() && /* @__PURE__ */ jsx("div", { sel: ".sankey-node-lift", class: { "negative-lift": negativeLift } }, /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: props.getLiftIcon(negativeLift) } }), `${lift}%`, /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: Placement.RightCenter, "max-width": `300px` } }, props.getLiftTooltipText(lift, negativeLift)))))))), node.segments.map((segment) => /* @__PURE__ */ jsx("div", { sel: ".sankey-segment-value.sankey-node-name" }, `${segment.value}`, /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: Placement.RightCenter, "max-width": `300px` } }, `${segment.label}: ${segment.value}`))))
  ));
};
export function template() {
  const { linkTooltip, links, nodeTooltip, selectedBreakdown, selectedNode, topPath } = this.state;
  const { t } = this.getContext(`i18n`);
  const steps = this.data.steps;
  const breakdowns = this.data.breakdowns;
  const addStepsText = t(`flows.add-steps`);
  const anchorStep = this.helpers.getFlowAnchorStep();
  return /* @__PURE__ */ jsx(
    "div",
    {
      sel: ".sankey",
      class: {
        "is-loaded": steps && !this.helpers.isEmptyFlow() ? links !== null : true,
        "is-reverse": this.attr(`reverse`),
        "is-top-path-mode": topPath.size > 0
      },
      hook: {
        insert: this.helpers.onSankeyInsert
      }
    },
    steps && this.helpers.isEmptyFlow() && /* @__PURE__ */ jsx("slot", { attrs: { name: `empty-state` } }),
    steps && !this.helpers.isEmptyFlow() && /* @__PURE__ */ jsx(null, null, linkTooltip ? /* @__PURE__ */ jsx(LinkTooltipPanel, { ...linkTooltip, renameEvents: this.helpers.renameEvents }) : nodeTooltip ? /* @__PURE__ */ jsx(
      ChartTooltipEl,
      {
        sections: nodeTooltip.sections,
        positioning: { clientX: nodeTooltip.clientX, clientY: nodeTooltip.clientY },
        renderInPortal: true
      }
    ) : null, /* @__PURE__ */ jsx(
      ScrollControlEl,
      {
        attrs: {
          "scroll-by": this.helpers.getScrollByAmount()
        },
        props: {
          mousemoveEl: this.helpers.getMousemoveElement(),
          scrollEl: this.helpers.getScrollElement()
        }
      }
    ), /* @__PURE__ */ jsx(
      "div",
      {
        sel: ".sankey-table",
        hook: {
          insert: this.helpers.onTableInserted,
          postpatch: this.helpers.onTablePatched
        }
      },
      breakdowns.length > 1 && /* @__PURE__ */ jsx("div", { sel: ".sankey-legend" }, /* @__PURE__ */ jsx(
        ChartLegendEl,
        {
          theme: ChartLegendTheme.CardTable,
          sections: this.helpers.getBreakdownSections(),
          wrapLabels: true
        }
      )),
      /* @__PURE__ */ jsx("div", { sel: ".sankey-table-scroller", key: "sankey", hook: { insert: this.helpers.onScrollerInsert } }, /* @__PURE__ */ jsx("div", { sel: ".sankey-table-content", hook: { insert: this.helpers.onTableContentInserted } }, /* @__PURE__ */ jsx("div", { sel: ".sankey-table-headers" }, steps.map((step, stepIndex) => {
        const isSingleStep = steps.length <= 1;
        const isLast = stepIndex === steps.length - 1;
        const isFirst = stepIndex === 0;
        const removeStepsByColumn = (step.isForward || step.isReverse) && !this.attr(`disable-add-step`);
        const inRemoveRange = this.helpers.isInRemoveRange(step);
        return /* @__PURE__ */ jsx(null, null, step.canAddReverseStep && !this.attr(`disable-add-step`) && /* @__PURE__ */ jsx(
          "div",
          {
            sel: ".sankey-table-column.break-header.reverse",
            class: { "is-first": isFirst, "is-single-step": isSingleStep }
          },
          /* @__PURE__ */ jsx("div", { sel: ".button-option-container" }, /* @__PURE__ */ jsx(
            SelectEl,
            {
              attrs: {
                icon: { leftIcon: `plus` },
                "icon-only": !isSingleStep,
                "selected-option": addStepsText,
                "menu-width": null,
                "never-show-caret": true,
                "disable-search": true,
                sections: this.helpers.getAddStepsSections(),
                theme: SelectTheme.Tertiary
              },
              on: {
                change: (ev) => this.helpers.updateSteps({
                  operation: `add`,
                  type: `reverse`,
                  anchor: step.anchor,
                  absolute: this.helpers.getAbsoluteSteps({
                    step,
                    operation: `add`,
                    value: ev.detail.item.value
                  })
                })
              }
            }
          ), !isSingleStep && /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: isFirst ? Placement.Right : Placement.Bottom, offset: 5 } }, /* @__PURE__ */ jsx("span", null, addStepsText)))
        ), /* @__PURE__ */ jsx(
          "div",
          {
            sel: ".sankey-table-column",
            class: {
              "in-remove-range": inRemoveRange,
              "highlight-for-removal": removeStepsByColumn,
              "has-context_menu": false,
              "is-step-column": true,
              "is-last": isLast
            },
            on: {
              click: () => removeStepsByColumn ? this.helpers.updateSteps({
                operation: `remove`,
                type: step.isForward ? `forward` : `reverse`,
                anchor: step.anchor,
                absolute: this.helpers.getAbsoluteSteps({
                  step,
                  operation: `remove`,
                  value: 1
                })
              }) : {},
              mouseenter: () => removeStepsByColumn ? this.helpers.onRemoveStepHover(step) : {},
              mouseleave: () => removeStepsByColumn ? this.helpers.onRemoveStepHover(step) : {}
            }
          },
          /* @__PURE__ */ jsx("div", { sel: ".sankey-header-content" }, stepIndex !== 0 ? /* @__PURE__ */ jsx("div", { sel: ".sankey-header-arrow-white" }) : null, /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-label-wrapper", class: { "is-first": isFirst } }, this.attr(`disable-anchor-event-labels`) ? /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-name" }, "Step ", stepIndex + 1) : /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-letter-label" }, step.letterLabel), /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-name" }, (() => {
            const originalLabel = step.getStepLabel();
            const stepLabel = originalLabel && originalLabel.length > 20 ? this.helpers.truncateLabel(originalLabel, 20) : originalLabel;
            return /* @__PURE__ */ jsx(null, null, stepLabel, originalLabel && originalLabel.length > 20 ? /* @__PURE__ */ jsx(
              TooltipEl,
              {
                attrs: {
                  placement: Placement.Bottom,
                  offset: -5,
                  "max-width": `300px`
                }
              },
              /* @__PURE__ */ jsx("span", null, originalLabel)
            ) : null);
          })()))), removeStepsByColumn ? /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-label-wrapper.remove-events" }, /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-letter-label" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: `dash` } })), /* @__PURE__ */ jsx("div", { sel: ".sankey-header-column-name" }, step.canAddForwardStep || step.canAddReverseStep ? t(`flows.remove-step`) : t(`flows.remove-steps`))) : null, stepIndex !== steps.length - 1 ? /* @__PURE__ */ jsx("div", { sel: ".sankey-header-arrow-gray" }) : null)
        ), step.canAddForwardStep && !this.attr(`disable-add-step`) && /* @__PURE__ */ jsx(
          "div",
          {
            sel: ".sankey-table-column.break-header.forward",
            class: { "is-single-step": isSingleStep, "is-last": isLast }
          },
          /* @__PURE__ */ jsx("div", { sel: ".button-option-container" }, /* @__PURE__ */ jsx(
            SelectEl,
            {
              attrs: {
                icon: { leftIcon: `plus` },
                "icon-only": !isSingleStep,
                "selected-option": addStepsText,
                "menu-width": null,
                "disable-search": true,
                "never-show-caret": true,
                sections: this.helpers.getAddStepsSections(),
                theme: SelectTheme.Tertiary
              },
              props: {
                boundaryEl: this.helpers.getScrollElement()
              },
              on: {
                change: (ev) => this.helpers.updateSteps({
                  operation: `add`,
                  type: `forward`,
                  anchor: step.anchor,
                  absolute: this.helpers.getAbsoluteSteps({
                    step,
                    operation: `add`,
                    value: ev.detail.item.value
                  })
                })
              }
            }
          ), !isSingleStep ? /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: isLast ? Placement.Left : Placement.Bottom, offset: 5 } }, /* @__PURE__ */ jsx("span", null, addStepsText)) : null)
        ), step.canAddForwardStep && stepIndex < steps.length - 1 && /* @__PURE__ */ jsx("div", { sel: ".sankey-table-column.break-header.break" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: `wave` } }), /* @__PURE__ */ jsx("div", { sel: ".break" })));
      })), /* @__PURE__ */ jsx("div", { sel: ".sankey-table-body" }, /* @__PURE__ */ jsx("svg", { sel: ".sankey-links" }, (links || []).map((link) => /* @__PURE__ */ jsx(
        "path",
        {
          attrs: link.attrs,
          class: link.class,
          props: link.props,
          on: link.on,
          style: { "--breakdown-bg-color": link.color }
        }
      ))), steps.map((step, stepIndex) => {
        const isSingleStep = steps.length <= 1;
        const isLast = stepIndex === steps.length - 1;
        const isFirst = stepIndex === 0;
        return /* @__PURE__ */ jsx(null, null, step.canAddReverseStep && !this.attr(`disable-add-step`) && /* @__PURE__ */ jsx(
          "div",
          {
            sel: ".sankey-table-column.break-body.reverse",
            class: { "is-first": isFirst, "is-single-step": isSingleStep }
          }
        ), /* @__PURE__ */ jsx("div", { sel: ".sankey-table-column", class: { "is-step-column": true, "is-last": isLast } }, step.nodes.length > 0 ? step.nodes.map((node) => {
          const nodeAttributes = this.helpers.getNodeAttributes(node, step);
          const disableClick = !!this.attr(`disable-click-node`);
          return /* @__PURE__ */ jsx("div", { sel: ".sankey-node-wrapper" }, /* @__PURE__ */ jsx(
            NodeLabel,
            {
              node,
              nodeAttributes,
              anchorStepWeight: anchorStep.weight,
              disableLift: this.attr(`disable-lift`),
              disableAnchorEventLabels: this.attr(`disable-anchor-event-labels`),
              renameEvents: this.helpers.renameEvents,
              getAnchorLabelOverride: this.helpers.getAnchorLabelOverride,
              getLabelIcon: this.helpers.getLabelIcon,
              getLiftIcon: this.helpers.getLiftIcon,
              getLiftTooltipText: this.helpers.getLiftTooltipText,
              getWeightTooltipText: this.helpers.getWeightTooltipText
            }
          ), /* @__PURE__ */ jsx(
            "div",
            {
              sel: ".sankey-node",
              class: {
                "is-in-top-path": nodeAttributes.isInTopPath,
                [`is-${nodeAttributes.nodeType}`]: true,
                "disable-click": disableClick
              },
              dataset: { id: node.id },
              props: { node }
            },
            node.breakdowns.map((breakdown) => {
              const breakdownAttributes = this.helpers.getBreakdownAttributes(breakdown);
              return /* @__PURE__ */ jsx(
                "div",
                {
                  sel: ".sankey-node-breakdown",
                  class: {
                    "is-active": selectedNode === node.id && selectedBreakdown === breakdown.id,
                    "in-remove-range": nodeAttributes.isInRemoveRange,
                    "is-broken-down": breakdowns.length > 1,
                    [`is-${nodeAttributes.nodeType}`]: true
                  },
                  style: {
                    height: `${breakdownAttributes.height}px`,
                    "--breakdown-bg-color": breakdownAttributes.colors.primary,
                    "--breakdown-text-color": getAccessibleForegroundColor(
                      breakdownAttributes.colors.primary
                    )
                  },
                  on: {
                    click: (ev) => this.helpers.onBreakdownNodeClicked(ev, node, breakdown),
                    mousemove: (ev) => this.helpers.onBreakdownNodeMouseMove(ev, node, breakdown),
                    mouseleave: this.helpers.onBreakdownNodeMouseLeave
                  }
                },
                breakdowns.length > 1 && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".sankey-node-breakdown-nudger" }), /* @__PURE__ */ jsx("div", { sel: ".sankey-node-breakdown-percentage" }, `${formatMetricNumber(
                  breakdown.totalCount / node.totalCount * 100
                )}%`))
              );
            })
          ));
        }) : /* @__PURE__ */ jsx("div", { sel: ".sankey-empty-column" }, `No Data`)), step.canAddForwardStep && !this.attr(`disable-add-step`) && /* @__PURE__ */ jsx(
          "div",
          {
            sel: ".sankey-table-column.break-body.forward",
            class: { "is-single-step": isSingleStep, "is-last": isLast }
          }
        ), step.canAddForwardStep && stepIndex < steps.length - 1 && /* @__PURE__ */ jsx("div", { sel: ".sankey-table-column.break-body.break" }, /* @__PURE__ */ jsx("div", { sel: ".break" })));
      }))))
    ))
  );
}
